<script lang="ts" setup>
import { getNormalizedLegacyFeedbackHash, removeLegacyFeedbackTab, resolveFeedbackTab } from '~/utils/feedbackUrl';

type Tab = { title: string; slug: string; count: number };

const feedbackStore = useFeedbackStore();
const { reviewsPagination, questionsPagination } = storeToRefs(feedbackStore);
const isHydrated = useHydratedFlag();

const tabs = computed(() => [
  { title: 'Отзывы', slug: 'reviews', count: isHydrated.value ? (reviewsPagination.value?.total ?? 0) : 0 },
  { title: 'Вопросы', slug: 'questions', count: isHydrated.value ? (questionsPagination.value?.total ?? 0) : 0 },
]);

const selectedTab = ref(tabs.value[0]);
const route = useRoute();
let isFeedbackMounted = false;

async function scrollToHash(hash: string) {
  if (!import.meta.client || !hash) return;

  await nextTick();
  document.getElementById(hash.slice(1))?.scrollIntoView();
}

async function syncFeedbackState(hash: string) {
  const tabSlug = resolveFeedbackTab(hash, route.query.tab);
  selectedTab.value = tabs.value.find((tab) => tab.slug === tabSlug) || tabs.value[0];
  await scrollToHash(hash);
}

function handleHashChange() {
  void syncFeedbackState(window.location.hash);
}

async function switchTab(tab: Tab) {
  selectedTab.value = tab;
  await navigateTo({
    path: route.path,
    query: removeLegacyFeedbackTab(route.query),
    hash: `#${tab.slug}`,
  });
  await scrollToHash(`#${tab.slug}`);
}

watch(
  () => [route.hash, route.query.tab],
  () => syncFeedbackState(import.meta.client && isFeedbackMounted ? window.location.hash : ''),
  { immediate: true },
);

onMounted(async () => {
  isFeedbackMounted = true;
  window.addEventListener('hashchange', handleHashChange);
  window.addEventListener('pageshow', handleHashChange);
  window.addEventListener('load', handleHashChange);
  await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

  const browserHash = window.location.hash;
  await syncFeedbackState(browserHash);

  const normalizedHash = getNormalizedLegacyFeedbackHash(browserHash, route.query.tab);
  if (!normalizedHash) return;

  await navigateTo(
    {
      path: route.path,
      query: removeLegacyFeedbackTab(route.query),
      hash: normalizedHash,
    },
    { replace: true },
  );
});

onBeforeUnmount(() => {
  window.removeEventListener('hashchange', handleHashChange);
  window.removeEventListener('pageshow', handleHashChange);
  window.removeEventListener('load', handleHashChange);
});

const { selectedOffer } = storeToRefs(useCatalogStore());

watch(
  () => selectedOffer.value.product_id,
  (oldValue, newValue) => {
    if (oldValue !== newValue) feedbackStore.$reset();
  },
);
</script>

<template>
  <section class="product-page-feedback-block">
    <div class="product-page-feedback-block__tabs">
      <button
        v-for="tab in tabs"
        :key="tab.slug"
        :class="{ 'product-page-feedback-block__tab--selected': selectedTab.slug === tab.slug }"
        class="product-page-feedback-block__tab"
        @click="switchTab(tab)"
      >
        <h2 class="leading-none">{{ tab.title }}</h2>
        <span class="text-black-60 text-sm sm:text-base !leading-none sm:!leading-tight"> {{ tab.count }}</span>
      </button>
    </div>

    <ProductPageReviewsBlock v-show="selectedTab.slug === 'reviews'" />
    <ProductPageQuestionsBlock v-show="selectedTab.slug === 'questions'" />
  </section>
</template>

<style lang="scss" scoped>
.product-page-feedback-block {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;

  &__tabs {
    display: flex;
  }

  &__tab {
    display: flex;
    gap: 8px;
    justify-content: center;
    align-items: end;
    padding: 12px 16px;
    border-bottom: 2px solid $black-20;
    color: $black-60;
    transition: all 0.2s;

    &:hover {
      border-color: $black-40;
      color: $black-80;
    }

    &--selected {
      border-color: $black;
      color: $black;
    }
  }
}
</style>
